import { call, put, takeLatest } from "redux-saga/effects";
import { IBaseResponse } from "../../../interfaces/base";
import {
  ICreateTrainerPayload,
  IGetTrainerAction,
} from "../../../interfaces/store/trainer-interfaces";
import {
  getTrainerFailed,
  getTrainerSuccess,
  getTrainerStart,
  createTrainerStart,
  createTrainerSuccess,
  createTrainerFailed,
  updateTrainerStart,
  updateTrainerSuccess,
  updateTrainerFailed,
} from "./trainer-slice";
import {
  getFitnessProfileByUserIdApi,
  getUserByIdApi,
  updateUserApi,
} from "../../../api/accounts";
import { createTrainerApi } from "../../../api/trainers";
import { PayloadAction } from "@reduxjs/toolkit";
import { uploadImage } from "../../utils/fileUpload";
import { base64ToBlob } from "../../../utils/utils";

function* _getTrainer({ payload }: IGetTrainerAction) {
  try {
    const { trainerId } = payload;

    if (!trainerId) {
      yield put(getTrainerFailed({ message: "Trainer ID is required" }));
      return;
    }

    if (trainerId === "add") {
      yield put(getTrainerSuccess({ data: null }));
      return;
    }

    const trainer: IBaseResponse = yield call(getUserByIdApi, trainerId);

    if (trainer.error) {
      yield put(getTrainerFailed(trainer.error));
    }

    const trainerFitnessProfile: IBaseResponse = yield call(
      getFitnessProfileByUserIdApi,
      trainerId
    );

    yield put(
      getTrainerSuccess({
        data: { ...trainer.data, fitnessProfile: trainerFitnessProfile.data },
      })
    );
  } catch (error: any) {
    yield put(getTrainerFailed({ message: error.message }));
  }
}

function* _createTrainer({ payload }: PayloadAction<ICreateTrainerPayload>) {
  try {
    const response: IBaseResponse = yield call(createTrainerApi, {
      ...payload.trainer,
    });

    const { imageData } = payload;

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile as string),
        }
      : undefined;

    if (blobImageData && response.data.id) {
      if (blobImageData.binaryFile) {
        yield call(uploadImage, blobImageData, response.data.id);
      }
      yield call(_updateTrainer, {
        payload: {
          trainer: {
            account: {
              avatar: blobImageData.binaryFile
                ? response.data.id + "/" + blobImageData.fileName
                : null,
            },
          },
          userId: response.data.id,
        },
      });
    }

    if (response.error) {
      yield put(createTrainerFailed(response.error));
    } else {
      yield put(createTrainerSuccess(response.data));
    }
  } catch (error: any) {
    if (
      error?.status === 409 &&
      error?.response?.data?.error === "TRAINER EMAIL ALREADY EXISTS"
    ) {
      yield put(
        createTrainerFailed({
          message: "This email is already registered",
          status: 409,
        })
      );
      return;
    }

    yield put(createTrainerFailed({ message: error.message }));
  }
}

function* _updateTrainer({ payload }: any) {
  try {
    const { imageData } = payload;

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile),
        }
      : undefined;

    if (blobImageData && blobImageData.binaryFile) {
      yield call(uploadImage, blobImageData, payload.userId);
    }

    const updateBody = { ...payload.trainer };

    if (blobImageData?.fileName) {
      updateBody.account.avatar = payload.userId + "/" + blobImageData.fileName;
    } else if (blobImageData && blobImageData.binaryFile === null) {
      updateBody.account.avatar = null;
    }

    const response: IBaseResponse = yield call(
      updateUserApi,
      updateBody,
      payload.userId
    );

    if (response.error) {
      yield put(updateTrainerFailed(response.error));
    } else {
      yield put(updateTrainerSuccess(response.data));
    }
  } catch (error: any) {
    yield put(updateTrainerFailed({ message: error.message }));
  }
}

function* trainerSaga() {
  yield takeLatest(getTrainerStart.type, _getTrainer);
  yield takeLatest(createTrainerStart.type, _createTrainer);
  yield takeLatest(updateTrainerStart.type, _updateTrainer);
}

export default trainerSaga;
